import React from 'react';
import {
  FiEdit2 as Edit,
  FiTrash2 as Trash,
  FiToggleLeft as ToggleOff,
  FiToggleRight as ToggleOn,
} from 'react-icons/fi';

const METRIC_LABELS: Record<string, string> = {
  heap_percent: 'Heap Usage (%)',
  heap_used: 'Heap Used (bytes)',
  rss: 'RSS Memory',
  cpu_usage_percent: 'CPU Usage (%)',
  thread_count: 'Thread Count',
  thread_active: 'Active Threads',
  thread_blocked: 'Blocked Threads',
};

const CONDITION_LABELS: Record<string, string> = {
  '>': 'greater than',
  '>=': 'greater than or equal',
  '<': 'less than',
  '<=': 'less than or equal',
  '==': 'equals',
};

export interface AlertRule {
  id: string;
  name: string;
  description?: string;
  metric_type: string;
  condition: string;
  threshold: number;
  severity: string;
  agent_name_pattern?: string | null;
  enabled: boolean;
  cooldown_seconds: number;
}

interface Props {
  rules?: AlertRule[];
  loading?: boolean;
  onEdit?: (rule: AlertRule) => void;
  onDelete?: (id: string) => void;
  onToggle?: (id: string, enabled: boolean) => void;
}

const APMAlertRulesTable: React.FC<Props> = ({
  rules = [],
  loading = false,
  onEdit,
  onDelete,
  onToggle,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (rules.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No alert rules configured. Click &ldquo;Add Rule&rdquo; or &ldquo;Seed Defaults&rdquo; to get started.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Name
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Metric
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Condition
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Severity
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Agent Filter
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Enabled
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {rules.map((rule) => (
            <tr key={rule.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {rule.name}
                </div>
                {rule.description && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                    {rule.description}
                  </div>
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                {METRIC_LABELS[rule.metric_type] ?? rule.metric_type}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                {CONDITION_LABELS[rule.condition] ?? rule.condition} {rule.threshold}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    rule.severity === 'critical'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                  }`}
                >
                  {rule.severity}
                </span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                {rule.agent_name_pattern ?? 'All agents'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-center">
                <button
                  onClick={() => onToggle?.(rule.id, !rule.enabled)}
                  className={`p-1 rounded transition-colors ${
                    rule.enabled
                      ? 'text-green-600 hover:text-green-700 dark:text-green-400'
                      : 'text-gray-400 hover:text-gray-500'
                  }`}
                  title={rule.enabled ? 'Disable rule' : 'Enable rule'}
                >
                  {rule.enabled ? (
                    <ToggleOn className="w-6 h-6" />
                  ) : (
                    <ToggleOff className="w-6 h-6" />
                  )}
                </button>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => onEdit?.(rule)}
                    className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                    title="Edit rule"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete?.(rule.id)}
                    className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                    title="Delete rule"
                  >
                    <Trash className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default APMAlertRulesTable;
